import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksChipRemoveEvent, BlocksChipSelectEvent } from '../../events';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../icon/icons/check';
import '../icon/icons/close';
import '../skeleton/skeleton';
import type { ChipSize } from './chip.model';
import styles from './chip.styles';
/**
 * @slot select - The component select content when `isActive` is true and `removable` is false.Default is cat-icon-check
 * @slot default - The component default content.
 * @slot close - The component close content when `removable` is true.Default is cat-icon-close
 * @fires bl-chip-select - emitted when chip is selected
 * @fires bl-chip-remove - emitted when chip is removed
 * @csspart base - The component's base component.
 * @csspart part-select - The component's select slot.
 * @csspart check-icon - The component's `<cat-icon-check>` icon.
 * @csspart label - The component's label.
 * @csspart part-default - The component's default slot.
 * @csspart close-wrapper - The component's close icon wrapper.
 * @csspart wrapper - The component's wrapper.
 * @csspart part-close - The component's close slot.
 * @csspart close-icon - The component's `<cat-icon-close>`.
 * @csspart skeleton - The component's skeleton wrapper.
 * @csspart loader - The component's loader wrapper.
 * @csspart loader-body - The component's loader body.
 */

export class BlocksChip extends BlocksElement {static styles: CSSResultGroup = styles;

  /** Makes the chips removable and shows a remove icon. */
  @property({ attribute: true, type: Boolean })
  removable = false;

  /**
   * Size
   * <ul>
   * <li> **default** the standard size for the chip</li>
   * <li> **sm** small size chip</li>
   * </ul>
   */
  @property({ type: String })
  size: ChipSize;

  /**
   * Append an active state on click
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * disabled state for chips
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * Enables the readonly on Chip
   */
  @property({ type: Boolean })
  isReadonly = false;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * This property is designed to be announced by  screen reader,mostly to announce the behavior/state of the chip 
     whether the chip is removable, selectable, loading or readonly.
     e.g: When the chip is removable and the user wishes for it to be announced as deletable, the `chipLabel` can be set to 
     "deletable". Intended solely for accessibility purposes
   */
  @property({ type: String })
  chipLabel: string | undefined;

  /**
   *The text content provided to this property will be announced by  screen reader as an alternative to the default text content provided for the chip. 
    Intended solely for accessibility purposes. Screen reader will prioritize announcing the text content provided for this property over the actual text inside the chip.
   */
  @property({ type: String })
  chipTextContent: string | undefined;

  /**
   * To set the role for the chip
   */
  @property({ type: String })
  chipRole: string | undefined;

  private chipText?: string;
  private chipButtonRole?: string;

  private get isNotReadonlyAndDisabled(): boolean {
    return !this.disabled && !this.isReadonly;
  }

  /**
   * Click function
   * Toggles the active state when triggered if not removable
   */
  private handleClick() {
    if (!this.removable && this.isNotReadonlyAndDisabled) {
      this.isActive = !this.isActive;
      this.fireChipSelectEvent();
    }
  }

  private onRemove(evt: MouseEvent): void {
    evt.stopPropagation();
    if (this.isNotReadonlyAndDisabled) {
      this.fireChipRemoveEvent();
    }
  }

  /**
   * If it is removable chip variant, upon focusing close icon and keying Enter / space / delete / escape on close icon should fire bl-chip-remove
   */
  private handleKeyDownOnCloseIcon(evt: KeyboardEvent) {
    if ([KEYS.ESCAPE, KEYS.DELETE].includes(evt.key) && this.isNotReadonlyAndDisabled) {
      this.fireChipRemoveEvent();
    }
  }

  /**
   * set the default chip role and corresponding chip label to be announced by screen reader
   */
  protected willUpdate(): void {
    if (this.isLoading) {
      this.chipText = 'loading';
    } else {
      const baseText = this.chipTextContent || '';
      this.chipText =
        this.isReadonly && !this.removable
          ? `${baseText} selectable readonly`
          : this.isReadonly && this.removable
          ? `${baseText} removable readonly`
          : `${baseText}${this.removable ? ' removable' : ' selectable'}`;
    }

    this.chipButtonRole = this.removable || this.disabled ? 'button' : 'option';
  }

  /**
   *  @fire bl-chip-select
   */
  private fireChipSelectEvent() {
    this.dispatch({
      eventName: 'bl-chip-select',
      detailObj: { isActive: this.isActive }
    }) as BlocksChipSelectEvent;
  }

  /**
   *  @fire bl-chip-remove
   */
  private fireChipRemoveEvent() {
    this.dispatch({
      eventName: 'bl-chip-remove'
    }) as BlocksChipRemoveEvent;
  }

  private renderLoading() {
    const componentClassNamesForLoading = this.componentClassNames('cat-c-chip--loader', {
      'cat-c-chip--loader-small': this.size === 'sm',
      'cat-c-chip--loader-removable': this.removable
    });
    return html`<div part="base" class="${componentClassNamesForLoading}">
      <cat-skeleton part="skeleton" arialabel=${ifDefined(this.chipLabel ?? this.chipText)} exportparts=${this.getExportParts('skeleton')}>
        <div part="loader" class="cat-c-chip--loader-section">
          <div part="loader-body" class="cat-c-chip--loader-body"></div>
          <cat-icon-close
            part="close-icon"
            exportparts=${this.getExportParts('close', 'icon')}
            size=${ifDefined(this.size === 'sm' ? 'sm' : undefined)}
          ></cat-icon-close>
        </div>
      </cat-skeleton>
    </div>`;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-chip', {
      'cat-c-chip-is-active': this.isActive === true && !this.removable && !this.isReadonly,
      'cat-c-chip--small': this.size === 'sm',
      'cat-c-chip-removable': this.removable,
      'cat-c-chip-is-disabled': this.disabled === true,
      'cat-c-chip-is-readonly': this.isReadonly
    });

    if (this.isLoading) {
      return this.renderLoading();
    }

    return html`
      <li role="none" class="cat-c-chip__item">
        <label id="chip-label" class="cat-u-is-vishidden" aria-hidden="true"
          >${this.chipLabel ? (this.chipTextContent ? this.chipTextContent + ' ' + this.chipLabel : this.chipLabel) : this.chipText}</label
        >
        <div part="wrapper" class="cat-c-chip__wrapper">
          <button
            part="base"
            role=${ifDefined(this.chipRole ?? this.chipButtonRole)}
            class="${componentClassNames}"
            id="chip-button"
            @click=${this.handleClick}
            aria-selected=${ifDefined(!this.removable ? (this.isActive ? 'true' : 'false') : undefined)}
            aria-labelledby="chip-button chip-label"
            ?disabled=${this.disabled}
            tabindex=${this.removable ? -1 : 0}
            inert=${ifDefined(this.removable ? true : undefined)}
          >
            ${this.isActive && !this.removable
              ? html`
                  <slot part="part-select" name="select">
                    <cat-icon-check
                      part="check-icon"
                      exportparts=${this.getExportParts('check', 'icon')}
                      size=${ifDefined(this.size === 'sm' ? 'sm' : undefined)}
                    ></cat-icon-check>
                  </slot>
                `
              : nothing}
            <div part="label" class="cat-c-chip__label" aria-hidden=${ifDefined(this.chipTextContent ? 'true' : undefined)}>
              <slot part="part-default"></slot>
            </div>
          </button>
          ${this.removable
            ? html`
                <button
                  part="close-wrapper"
                  class="cat-c-chip-close__button ${this.isReadonly ? 'cat-c-chip-close__button-readonly' : ''}"
                  aria-labelledby=${this.chipTextContent ? 'chip-label' : 'chip-button chip-label'}
                  @click=${this.onRemove}
                  @keydown=${this.handleKeyDownOnCloseIcon}
                  ?disabled=${this.disabled}
                  tabindex=${ifDefined(!this.disabled ? undefined : -1)}
                >
                  <slot part="part-close" name="close">
                    <cat-icon-close
                      part="close-icon"
                      exportparts=${this.getExportParts('close', 'icon')}
                      size=${ifDefined(this.size === 'sm' ? 'sm' : undefined)}
                    ></cat-icon-close>
                  </slot>
                </button>
              `
            : nothing}
        </div>
      </li>
    `;
  }
}

if (customElements.get('cat-chip') === undefined) {
  customElements.define('cat-chip', BlocksChip);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-chip': BlocksChip;
  }
}
